import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Alert, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';

interface LegalText {
  kind: string;
  title: string;
  content: string | null;
  url: string | null;
  version: number;
  updatedAt: string;
}

/** Öffentliche Anzeige eines Rechtstexts (AGB, Datenschutzerklärung, Widerrufsbelehrung) – ohne Anmeldung. */
export function LegalPage() {
  const { kind } = useParams<{ kind: string }>();
  const { data, error } = useQuery({ queryKey: ['public', 'legal', kind], queryFn: () => api<LegalText>(`/public/legal/${kind}`), retry: false });

  return (
    <div className="min-h-dvh bg-muted/30 px-4 py-8">
      <div className="mx-auto max-w-3xl">
        {error ? (
          <Alert>{error instanceof ApiError && error.status === 404 ? 'Dieser Text ist nicht hinterlegt.' : 'Der Text konnte nicht geladen werden.'}</Alert>
        ) : !data ? (
          <Skeleton className="h-96" />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">{data.title}</CardTitle>
              <p className="text-xs text-muted-foreground">
                Fassung {data.version} vom {formatDate(data.updatedAt)}
              </p>
            </CardHeader>
            <CardContent className="grid gap-4">
              {/* Inhalt wird beim Speichern serverseitig bereinigt (nur Formatierungen, keine Skripte) */}
              {data.content && <div className="mail-editor max-w-none text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: data.content }} />}
              {data.url && (
                <a href={data.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  {data.content ? 'Vollständiger Text auf unserer Website' : `${data.title} auf unserer Website öffnen`} <ExternalLink className="size-4" />
                </a>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
